<template>
  <AuthenticatedView :loading="userLoading" :current-user="currentUser" :has-result="!!userResult">
    <div>
      <AppHeader
        :user="currentUser"
        :profile-url="profileUrl"
        :chat-id="chatId"
        :from-id="fromId"
      />

      <div :class="['card', { 'full-width': isFullWidth }]">
        <DashboardPage
          :chat-id="chatId"
          :from-id="fromId"
          :has-telegram-user-id="hasTelegramUserId"
          :is-full-width="isFullWidth"
          :can-toggle-full-width="canToggleFullWidth"
          @toggle-full-width="toggleFullWidth"
        />
      </div>
    </div>
  </AuthenticatedView>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import AppHeader from '../common/AppHeader.vue'
import AuthenticatedView from '../common/AuthenticatedView.vue'
import DashboardPage from './DashboardPage.vue'
import { useCurrentUser } from '@/composables/useCurrentUser'
import { readFullWidthPreference, writeFullWidthPreference } from '@/utils/fullWidthPreference'

const props = defineProps({
  chatId: String,
  fromId: String,
})

const { currentUser, loading: userLoading } = useCurrentUser({
  fromId: props.fromId,
  removeLoadingOnComplete: true,
})

const userResult = computed(() => !userLoading.value)
const isFullWidth = ref(false)
const canToggleFullWidth = ref(false)

const hasTelegramInitUser = computed(() => {
  if (typeof window === 'undefined') return false
  const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user
  return !!tgUser?.id
})

const hasTelegramUserId = computed(
  () => !!props.fromId || hasTelegramInitUser.value || !!currentUser.value?.telegramUserId
)

const profileUrl = computed(() => {
  let url = '/profile'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

function checkCanToggleFullWidth() {
  if (typeof window === 'undefined') return
  canToggleFullWidth.value = window.innerWidth >= 1024
  isFullWidth.value = canToggleFullWidth.value && readFullWidthPreference()
}

function toggleFullWidth() {
  if (!canToggleFullWidth.value) return
  isFullWidth.value = !isFullWidth.value
  writeFullWidthPreference(isFullWidth.value)
}

onMounted(() => {
  checkCanToggleFullWidth()
  window.addEventListener('resize', checkCanToggleFullWidth)
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', checkCanToggleFullWidth)
  }
})
</script>

<style scoped>
.card.full-width {
  max-width: 100% !important;
  width: 100%;
  margin: 0;
  border-radius: 0;
}

.card {
  overflow: visible;
}
</style>
